/* eslint-disable no-useless-catch */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Row, Col, Spin, Tabs as Tab } from 'antd'
import { useUnsavedChangesPrompt } from '../../../hooks/useUnsavedChangesPrompt'
import DetailPageLayout from '../../layouts/DetailPageLayout'
import useDispatchHttp from '../../../hooks/dispatchHttpHandler'
import { useUser } from '../../../providers/UserProvider'
import { useUnsavedChanges } from '../../../providers/UnsavedChangesProvider'
import {
  selectInsuranceCompanyById,
  fetchInsuranceCompanyById,
  updateInsuranceCompany,
} from '../../../state/modules/insuranceCompanies'
import {
  selectAgencyById,
  fetchAgencyById,
  updateAgency,
} from '../../../state/modules/agencies'
import { LOADING_STATUS } from '../../../constants'
import Tabs from '../../components/Tabs'
import UnsavedFormChangesModal from '../../components/Modals/UnsavedFormChangesModal'
import OrganizationForm from '../../components/Forms/OrganizationForm/OrganizationForm'
import SamDataForm from '../../components/Forms/SamDataForm/SamDataForm'
import SamSettingsForm from '../../components/Forms/SamSettingsForm/SamSettingsForm'

const OrganizationDetails = () => {
  const dispatch = useDispatch()
  const dispatchHttp = useDispatchHttp()
  const { type, id } = useParams()
  const { isUserActionAllowed } = useUser()

  const [isAssignmentsFormDirty, setAssignmentsFormDirty] = useState(false)
  const [isSamDataFormDirty, setSamDataFormDirty] = useState(false)
  const isPageDirty = isAssignmentsFormDirty || isSamDataFormDirty

  const assignmentsFormRef = useRef(null)
  const samDataFormRef = useRef(null)

  const { isModalVisible, handleConfirmNavigation, handleCancelNavigation } =
    useUnsavedChangesPrompt(isPageDirty)

  const { registerPrompt } = useUnsavedChanges()
  const [isLogoutModalVisible, setLogoutModalVisible] = useState(false)
  const resolveLogoutPrompt = useRef(null)

  useEffect(() => {
    const showLogoutPrompt = () =>
      new Promise(resolve => {
        setLogoutModalVisible(true)
        resolveLogoutPrompt.current = resolve
      })

    if (isPageDirty) {
      registerPrompt(() => showLogoutPrompt)
    } else {
      registerPrompt(() => () => Promise.resolve(true))
    }

    return () => registerPrompt(() => () => Promise.resolve(true))
  }, [isPageDirty, registerPrompt])

  const [isTabChangeModalVisible, setTabChangeModalVisible] = useState(false)
  const [nextTabKey, setNextTabKey] = useState(null)

  const [activeTabKey, setActiveTabKey] = useState('assignments')

  const isInsurance = type === 'insurance-company'
  const selector = isInsurance ? selectInsuranceCompanyById : selectAgencyById
  const fetchAction = isInsurance ? fetchInsuranceCompanyById : fetchAgencyById
  const updateAction = isInsurance ? updateInsuranceCompany : updateAgency
  const editPermission = isInsurance ? 'change_insurancecompany' : 'change_agency'

  const organizationData = useSelector(state => selector(state, id))
  const status = useSelector(state =>
    isInsurance ? state.insuranceCompanies.status : state.agencies.status
  )

  useEffect(() => {
    if (id) {
      dispatch(fetchAction(id))
    }
  }, [dispatch, fetchAction, id])

  const breadcrumbs = useMemo(() => {
    const typeName = isInsurance ? 'Insurance Companies' : 'Agencies'
    const listPath = isInsurance ? '/organizations' : '/organizations?tab=1'
    const detailPath = `/organizations/${type}/${id}`

    return [
      { name: typeName, path: listPath },
      {
        name: organizationData?.name || '...',
        path: detailPath,
      },
    ]
  }, [organizationData, type, id, isInsurance])

  const handleAssignmentsSubmit = useCallback(
    async formData => {
      try {
        const changedFields = Object.keys(formData).reduce((acc, key) => {
          if (key === 'sam_data' || key === 'isRetrieverDisabled') {
            return acc
          }

          if (key === 'retriever_cms_id') {
            if (formData[key] !== (organizationData.retriever?.cms_id || '')) {
              acc[key] = formData[key] || null
            }
          } else if (formData[key] !== organizationData[key]) {
            if (typeof formData[key] === 'boolean') {
              acc[key] = formData[key]
            } else {
              acc[key] = formData[key] || null
            }
          }

          return acc
        }, {})

        if (Object.keys(changedFields).length === 0) return

        await dispatchHttp(
          updateAction({ id, data: changedFields }),
          'Organization has been successfully updated'
        )
      } catch (err) {
        throw err
      }
    },
    [dispatchHttp, updateAction, id, organizationData]
  )

  const handleSamDataSubmit = useCallback(
    async formData => {
      try {
        const processedData = {
          ...formData,
          email_remainder_interval: !Number.isNaN(
            parseInt(formData.email_remainder_interval, 10)
          )
            ? parseInt(formData.email_remainder_interval, 10)
            : null,
          email_remainder_emails_number: !Number.isNaN(
            parseInt(formData.email_remainder_emails_number, 10)
          )
            ? parseInt(formData.email_remainder_emails_number, 10)
            : null,
        }

        const payload = { sam_data: processedData }

        await dispatchHttp(
          updateAction({ id, data: payload }),
          'SAM data has been successfully updated'
        )
      } catch (err) {
        throw err
      }
    },
    [dispatchHttp, updateAction, id]
  )

  const handleSamSettingsSubmit = useCallback(
    async formData => {
      try {
        const payload = {
          sam_settings: {
            is_dob_required: !!formData?.is_dob_required,
            is_ssn_required: !!formData?.is_ssn_required,
          },
        }

        await dispatchHttp(
          updateAction({ id, data: payload }),
          'SAM settings have been successfully updated'
        )
      } catch (err) {
        throw err
      }
    },
    [dispatchHttp, updateAction, id]
  )

  const handleSaveAndNavigate = useCallback(async () => {
    const validationPromises = []
    const submissionHandlers = []

    if (isAssignmentsFormDirty) {
      validationPromises.push(assignmentsFormRef.current?.validateForm())
      submissionHandlers.push(() =>
        handleAssignmentsSubmit(assignmentsFormRef.current?.values)
      )
    }
    if (isSamDataFormDirty) {
      validationPromises.push(samDataFormRef.current?.validateForm())
      submissionHandlers.push(() => handleSamDataSubmit(samDataFormRef.current?.values))
    }

    try {
      const validationResults = await Promise.all(validationPromises)
      const allErrors = validationResults.reduce(
        (acc, errors) => ({ ...acc, ...errors }),
        {}
      )

      if (Object.keys(allErrors).length > 0) {
        if (isAssignmentsFormDirty)
          assignmentsFormRef.current?.setTouched(allErrors, true)
        if (isSamDataFormDirty) samDataFormRef.current?.setTouched(allErrors, true)

        handleCancelNavigation()
        return
      }

      const submissionPromises = submissionHandlers.map(handler => handler())
      await Promise.all(submissionPromises)

      handleConfirmNavigation()
    } catch (err) {
      handleCancelNavigation()
    }
  }, [
    isAssignmentsFormDirty,
    isSamDataFormDirty,
    handleAssignmentsSubmit,
    handleSamDataSubmit,
    handleConfirmNavigation,
    handleCancelNavigation,
  ])

  const handleTabChange = useCallback(
    newKey => {
      if (newKey === activeTabKey) return

      const isCurrentTabDirty =
        (activeTabKey === 'assignments' && isAssignmentsFormDirty) ||
        (activeTabKey === 'sam-data' && isSamDataFormDirty)

      if (isCurrentTabDirty) {
        setNextTabKey(newKey)
        setTabChangeModalVisible(true)
      } else {
        setActiveTabKey(newKey)
      }
    },
    [activeTabKey, isAssignmentsFormDirty, isSamDataFormDirty]
  )

  const handleCancelTabSwitch = useCallback(() => {
    setTabChangeModalVisible(false)
    setNextTabKey(null)
  }, [])

  const handleConfirmTabSwitch = useCallback(() => {
    if (activeTabKey === 'assignments' && isAssignmentsFormDirty) {
      assignmentsFormRef.current?.resetForm()
    }
    if (activeTabKey === 'sam-data' && isSamDataFormDirty) {
      samDataFormRef.current?.resetForm()
    }

    if (activeTabKey === 'assignments') setAssignmentsFormDirty(false)
    if (activeTabKey === 'sam-data') setSamDataFormDirty(false)
    setActiveTabKey(nextTabKey)
    handleCancelTabSwitch()
  }, [
    activeTabKey,
    nextTabKey,
    isAssignmentsFormDirty,
    isSamDataFormDirty,
    handleCancelTabSwitch,
  ])

  const handleSaveAndSwitch = useCallback(async () => {
    const validationPromises = []
    const submissionHandlers = []

    if (isAssignmentsFormDirty) {
      validationPromises.push(assignmentsFormRef.current?.validateForm())
      submissionHandlers.push(() =>
        handleAssignmentsSubmit(assignmentsFormRef.current?.values)
      )
    }
    if (isSamDataFormDirty) {
      validationPromises.push(samDataFormRef.current?.validateForm())
      submissionHandlers.push(() => handleSamDataSubmit(samDataFormRef.current?.values))
    }

    try {
      const validationResults = await Promise.all(validationPromises)
      const allErrors = validationResults.reduce(
        (acc, errors) => ({ ...acc, ...errors }),
        {}
      )

      if (Object.keys(allErrors).length > 0) {
        if (isAssignmentsFormDirty)
          assignmentsFormRef.current?.setTouched(allErrors, true)
        if (isSamDataFormDirty) samDataFormRef.current?.setTouched(allErrors, true)

        handleCancelTabSwitch()
        return
      }

      const submissionPromises = submissionHandlers.map(handler => handler())
      await Promise.all(submissionPromises)

      handleConfirmTabSwitch()
    } catch (err) {
      handleCancelTabSwitch()
    }
  }, [
    isAssignmentsFormDirty,
    isSamDataFormDirty,
    handleAssignmentsSubmit,
    handleSamDataSubmit,
    handleConfirmTabSwitch,
    handleCancelTabSwitch,
  ])

  const handleSave = useCallback(async () => {
    const validationPromises = []
    if (isAssignmentsFormDirty)
      validationPromises.push(assignmentsFormRef.current?.validateForm())
    if (isSamDataFormDirty)
      validationPromises.push(samDataFormRef.current?.validateForm())

    const validationResults = await Promise.all(validationPromises)
    const allErrors = validationResults.reduce(
      (acc, errors) => ({ ...acc, ...errors }),
      {}
    )

    if (Object.keys(allErrors).length > 0) {
      if (isAssignmentsFormDirty) assignmentsFormRef.current?.setTouched(allErrors, true)
      if (isSamDataFormDirty) samDataFormRef.current?.setTouched(allErrors, true)
      throw new Error('Validation failed')
    }

    const submissionPromises = []
    if (isAssignmentsFormDirty)
      submissionPromises.push(handleAssignmentsSubmit(assignmentsFormRef.current?.values))
    if (isSamDataFormDirty)
      submissionPromises.push(handleSamDataSubmit(samDataFormRef.current?.values))

    await Promise.all(submissionPromises)
  }, [
    isAssignmentsFormDirty,
    isSamDataFormDirty,
    handleAssignmentsSubmit,
    handleSamDataSubmit,
  ])

  const handleCancelLogout = () => {
    setLogoutModalVisible(false)
    if (resolveLogoutPrompt.current) resolveLogoutPrompt.current(false)
  }
  const handleConfirmLogout = () => {
    setLogoutModalVisible(false)
    if (resolveLogoutPrompt.current) resolveLogoutPrompt.current(true)
  }
  const handleSaveAndLogout = async () => {
    try {
      await handleSave()
      setLogoutModalVisible(false)
      if (resolveLogoutPrompt.current) resolveLogoutPrompt.current(true)
    } catch (err) {
      setLogoutModalVisible(false)
      if (resolveLogoutPrompt.current) resolveLogoutPrompt.current(false)
    }
  }

  return (
    <DetailPageLayout
      breadcrumbs={breadcrumbs}
      title={<span>{isInsurance ? 'Insurance Company' : 'Agency'}</span>}
    >
      <Spin spinning={status === LOADING_STATUS}>
        <Tabs
          fullWidth
          theme="dark"
          tabsProps={{
            size: 'small',
            tabBarGutter: 24,
            activeKey: activeTabKey,
            onChange: handleTabChange,
          }}
        >
          <Tab.TabPane tab="Assignments" key="assignments">
            <Row justify="center">
              <Col>
                {organizationData && (
                  <OrganizationForm
                    innerRef={assignmentsFormRef}
                    onDirtyChange={setAssignmentsFormDirty}
                    initialValues={organizationData}
                    onSubmit={handleAssignmentsSubmit}
                    isSubmitAllowed={isUserActionAllowed(editPermission)}
                  />
                )}
              </Col>
            </Row>
          </Tab.TabPane>
          <Tab.TabPane tab="SAM Data" key="sam-data">
            <Row justify="center">
              <Col>
                {organizationData?.sam_data && (
                  <SamDataForm
                    innerRef={samDataFormRef}
                    onDirtyChange={setSamDataFormDirty}
                    initialValues={organizationData.sam_data}
                    onSubmit={handleSamDataSubmit}
                    isSubmitAllowed={isUserActionAllowed(editPermission)}
                  />
                )}
              </Col>
            </Row>
          </Tab.TabPane>
          <Tab.TabPane tab="SAM Settings" key="sam-settings">
            <Row justify="center">
              <Col>
                <SamSettingsForm
                  initialValues={organizationData?.sam_settings}
                  onSubmit={handleSamSettingsSubmit}
                  isSubmitAllowed={isUserActionAllowed(editPermission)}
                />
              </Col>
            </Row>
          </Tab.TabPane>
        </Tabs>
      </Spin>

      {/* Modal for Page navigation */}
      <UnsavedFormChangesModal
        isVisible={isModalVisible}
        handleCancel={handleCancelNavigation}
        handleConfirm={handleConfirmNavigation}
        handleSave={handleSaveAndNavigate}
      />

      {/* Modal for TAB and LOGOUT navigation */}
      <UnsavedFormChangesModal
        isVisible={isTabChangeModalVisible || isLogoutModalVisible}
        handleCancel={
          isTabChangeModalVisible ? handleCancelTabSwitch : handleCancelLogout
        }
        handleConfirm={
          isTabChangeModalVisible ? handleConfirmTabSwitch : handleConfirmLogout
        }
        handleSave={isTabChangeModalVisible ? handleSaveAndSwitch : handleSaveAndLogout}
      />
    </DetailPageLayout>
  )
}

export default OrganizationDetails
